import * as React from 'react';
import {Remarkable} from 'remarkable';
import createDomPurify from 'dompurify';
import {defineMessages, FormattedMessage} from 'react-intl';
import {view, Button} from '@sail/ui';
import {
  Action,
  StripeAssistantAction,
  StripeAssistantCommandResponse,
} from '@stripe-internal/conversations-client';

const DOMPurify = createDomPurify();

const markdownRenderer = new Remarkable('full', {
  html: true,
  linkTarget: '_blank',
});

const messages = defineMessages({
  // Asking or Prompting messages
  askForWhatMatches: {
    id: 'conversations_ui.stripe_assistant.search_filter_results.ask_for_what_matches',
    description: 'Message for asking user what they want to view matches for',
    defaultMessage: 'What matches can I help you find?',
  },
  // Misc. Messages
  defaultDisclaimer1: {
    id: 'conversations_ui.stripe_assistant.search_filter_results.default_disclaimer_1',
    description: 'Disclaimer about AI Assistant',
    defaultMessage:
      'AI Assistant is currently unable to respond to this question.',
  },
  defaultDisclaimer2: {
    id: 'conversations_ui.stripe_assistant.search_filter_results.default_disclaimer_2',
    description: 'Disclaimer about AI Assistant',
    defaultMessage: 'Enter a new question to try again.',
  },
});

interface StripeAssistantSearchFiltersAction extends StripeAssistantAction {
  searchFilter: {
    query?: string;
    filters: Record<string, unknown>;
  };
}

interface StripeAssistantSearchFiltersCommandResult {
  text: string;
  actions: Array<StripeAssistantSearchFiltersAction>;
}

export interface StripeAssistantSearchFiltersResponse
  extends StripeAssistantCommandResponse {
  result: StripeAssistantSearchFiltersCommandResult;
}

type Props = {
  stripeAssistantResponse: StripeAssistantSearchFiltersResponse;
  requestAction: (action: Action) => void;
};

type ResultProps = {
  result: StripeAssistantSearchFiltersCommandResult;
  requestAction: (action: Action) => void;
};

function InvalidContent() {
  return (
    <>
      <FormattedMessage {...messages.defaultDisclaimer1} />
      <view.div css={{marginTop: 'medium'}}>
        <FormattedMessage {...messages.defaultDisclaimer2} />
      </view.div>
    </>
  );
}

function SearchFilterResultsActionButtons({
  actions,
  requestAction,
}: {
  actions: Array<StripeAssistantSearchFiltersAction>;
  requestAction: (action: Action) => void;
}) {
  const buttons = actions.map((action) => {
    const searchQueryParts = Object.keys(action.searchFilter.filters).map(
      (key) => {
        const value = action.searchFilter.filters[key];
        return `${key}:${value}`;
      },
    );

    if (action.searchFilter.query) {
      searchQueryParts.push(action.searchFilter.query);
    }

    const searchQueryString = searchQueryParts.join(' ');

    return (
      <Button
        key={action.humanText}
        onPress={() => {
          requestAction({
            name: 'set-dashboard-search-query',
            data: {
              query: searchQueryString,
            },
          });
        }}
      >
        {action.humanText}
      </Button>
    );
  });

  return (
    <view.div
      css={{
        stack: 'y',
        gap: 'small',
      }}
    >
      {buttons}
    </view.div>
  );
}

function ValidContent({result, requestAction}: ResultProps) {
  return (
    <view.div
      css={{
        stack: 'y',
        gap: 'medium',
      }}
    >
      <view.div
        css={{
          stack: 'y',
          gap: 'medium',
        }}
      >
        <view.span
          // Sanitize the html here so that we prevent any security risks
          //
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(
              markdownRenderer.renderInline(result.text),
            ),
          }}
        />
      </view.div>
      <SearchFilterResultsActionButtons
        actions={result.actions}
        requestAction={requestAction}
      />
    </view.div>
  );
}

export function SearchFilterResults(props: Props) {
  const {requestAction, stripeAssistantResponse} = props;
  const {result} = stripeAssistantResponse;

  const valid =
    result.text !== '' && result.actions && result.actions.length > 0;

  return (
    <view.div
      data-testid="ai-assistant-search-filters-drawer-content"
      css={{
        stack: 'y',
      }}
    >
      <view.div css={{marginTop: 'small', padding: 'small'}}>
        {valid ? (
          <ValidContent result={result} requestAction={requestAction} />
        ) : (
          <InvalidContent />
        )}
      </view.div>
    </view.div>
  );
}
